import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid, useGridApiRef, trTR, enUS } from '@mui/x-data-grid';
import { useTranslate, useLocaleState } from 'react-admin';
import { localizeGridColumnFilters } from '../../utils/localizeGridFilters';

/** MUI DataGrid (MIT) hard-caps pageSize at 100. */
const MAX_PAGE_SIZE = 100;
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const MIN_COLUMN_WIDTH = 72;

/**
 * MIT DataGrid: disableColumnResize FORCED true → native çekme yok.
 * Çizgi = native .columnSeparator (⋮ sağında); drag bizde.
 */
const RESPONSIVE_GRID_SX = {
  width: '100%',
  border: 0,
  '& .MuiDataGrid-cell': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiDataGrid-columnHeader': {
    position: 'relative',
    overflow: 'hidden',
  },
  // Sabit görünür çizgi — kolonun kendi içinde, ⋮ sağında
  '& .MuiDataGrid-columnSeparator': {
    display: 'flex !important',
    visibility: 'visible !important',
    opacity: '1 !important',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: '0 !important',
    left: 'auto !important',
    width: '14px !important',
    minWidth: '14px',
    zIndex: 10,
    cursor: 'col-resize !important',
    pointerEvents: 'auto !important',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'transparent !important',
  },
  '& .MuiDataGrid-columnSeparator--sideRight': {
    right: '0 !important',
  },
  '& .MuiDataGrid-iconSeparator': {
    display: 'none !important',
  },
  '& .MuiDataGrid-columnSeparator::after': {
    content: '""',
    display: 'block',
    width: 2,
    height: 16,
    borderRadius: '2px',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    pointerEvents: 'none',
  },
  '& .MuiDataGrid-columnSeparator:hover::after': {
    backgroundColor: '#1976d2',
    height: 20,
  },
};

const readStoredWidths = (storageKey) => {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const ColumnHeaderLabel = ({ label }) => (
  <Box
    component="span"
    title={label}
    sx={{
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
      pr: '14px',
    }}
  >
    {label}
  </Box>
);

const clampPageSize = (n) => Math.min(Math.max(Number(n) || 25, 1), MAX_PAGE_SIZE);

const sanitizePageSizeOptions = (options) => {
  const raw = Array.isArray(options) && options.length ? options : DEFAULT_PAGE_SIZE_OPTIONS;
  const capped = [...new Set(raw.map(clampPageSize))].sort((a, b) => a - b);
  return capped.length ? capped : DEFAULT_PAGE_SIZE_OPTIONS;
};

const DataGridTable = ({
  config,
  rows,
  columns,
  loading,
  onColumnsVisibilityChange,
  slots,
  slotProps,
  sx,
  onRowClick,
  getDetailPanelContent,
  getDetailPanelHeight,
  pageSizeOptions: pageSizeOptionsProp,
  autoHeight = true,
  enableColumnResize = true,
  ...rest
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const apiRef = useGridApiRef();
  const storageKey = config?.entity ? `gk-datagrid-widths:${config.entity}` : null;
  const [columnWidths, setColumnWidths] = useState(() => readStoredWidths(storageKey));

  useEffect(() => {
    setColumnWidths(readStoredWidths(storageKey));
  }, [storageKey]);

  const persistWidth = useCallback(
    (field, width) => {
      if (!field || width == null) return;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, width);
      setColumnWidths((prev) => {
        if (prev[field] === nextWidth) return prev;
        const next = { ...prev, [field]: nextWidth };
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(next));
        }
        return next;
      });
    },
    [storageKey],
  );

  // Custom resize: MIT native kapalı — separator mousedown yakala
  useEffect(() => {
    if (!enableColumnResize) return undefined;

    let detachMoveUp = null;

    const onSeparatorDown = (event) => {
      if (event.button !== 0) return;
      const sep = event.target.closest?.('.MuiDataGrid-columnSeparator');
      if (!sep) return;

      const root = apiRef.current?.rootElementRef?.current;
      if (root && !root.contains(sep)) return;

      const header = sep.closest('.MuiDataGrid-columnHeader');
      const field = header?.getAttribute('data-field');
      if (!field || !apiRef.current) return;

      event.preventDefault();
      event.stopPropagation();

      const column = apiRef.current.getColumn(field);
      const startWidth = column?.computedWidth ?? column?.width ?? MIN_COLUMN_WIDTH;
      const startX = event.clientX;
      let rafId = 0;
      let pendingWidth = startWidth;

      const applyWidth = (width) => {
        pendingWidth = width;
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          apiRef.current?.setColumnWidth?.(field, pendingWidth);
        });
      };

      const onMove = (moveEvent) => {
        applyWidth(Math.max(MIN_COLUMN_WIDTH, startWidth + (moveEvent.clientX - startX)));
      };

      const onUp = (upEvent) => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        detachMoveUp = null;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        const finalWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (upEvent.clientX - startX));
        apiRef.current?.setColumnWidth?.(field, finalWidth);
        persistWidth(field, finalWidth);
      };

      detachMoveUp = () => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    };

    // Capture: grid root veya document (root henüz yoksa)
    const attachTarget = () => apiRef.current?.rootElementRef?.current || null;
    let rootEl = attachTarget();
    const onDocDown = (e) => {
      const r = attachTarget();
      if (!r) return;
      if (!r.contains(e.target)) return;
      onSeparatorDown(e);
    };

    if (rootEl) {
      rootEl.addEventListener('mousedown', onSeparatorDown, true);
    } else {
      document.addEventListener('mousedown', onDocDown, true);
    }

    // Root sonra mount olabilir
    const timer = window.setInterval(() => {
      const next = attachTarget();
      if (next && next !== rootEl) {
        if (rootEl) rootEl.removeEventListener('mousedown', onSeparatorDown, true);
        else document.removeEventListener('mousedown', onDocDown, true);
        rootEl = next;
        rootEl.addEventListener('mousedown', onSeparatorDown, true);
        window.clearInterval(timer);
      }
    }, 200);

    return () => {
      window.clearInterval(timer);
      if (detachMoveUp) detachMoveUp();
      if (rootEl) rootEl.removeEventListener('mousedown', onSeparatorDown, true);
      document.removeEventListener('mousedown', onDocDown, true);
    };
  }, [apiRef, enableColumnResize, persistWidth]);

  const translatedColumns = useMemo(() => {
    if (!columns || !Array.isArray(columns)) return [];
    return columns.map((col) => {
      const fieldKey = col.field;
      const resourceKey = config?.entity || 'events-v2';
      const translatedHeader = translate(`resources.${resourceKey}.fields.${fieldKey}`, {
        _: col.headerName || fieldKey,
      });
      return localizeGridColumnFilters(
        {
          ...col,
          headerName: translatedHeader,
        },
        translate,
      );
    });
  }, [columns, config?.entity, translate, locale]);

  const gridColumns = useMemo(() => {
    return translatedColumns.map((column) => {
      const storedWidth = columnWidths[column.field];
      const hasStoredWidth = storedWidth != null;
      const resolvedWidth = hasStoredWidth ? storedWidth : (column.width ?? column.minWidth ?? 150);

      const nextColumn = {
        ...column,
        minWidth: column.minWidth ?? MIN_COLUMN_WIDTH,
        resizable: true,
        ...(hasStoredWidth || column.width != null
          ? { width: resolvedWidth, flex: undefined }
          : {}),
      };

      if (column.renderHeader) return nextColumn;

      return {
        ...nextColumn,
        renderHeader: () => <ColumnHeaderLabel label={column.headerName} />,
      };
    });
  }, [translatedColumns, columnWidths]);

  const pageSizeOptions = sanitizePageSizeOptions(
    pageSizeOptionsProp || config?.defaults?.pageSizeOptions,
  );
  const defaultPageSize = config?.defaults?.pageSize
    ? clampPageSize(config.defaults.pageSize)
    : undefined;

  const orderBy = config?.defaults?.orderBy;
  const sortModel =
    orderBy?.field && (orderBy.dir === 'asc' || orderBy.dir === 'desc')
      ? [{ field: orderBy.field, sort: orderBy.dir }]
      : undefined;

  const initialState =
    defaultPageSize || config?.defaults?.visible || sortModel
      ? {
          columns: {
            columnVisibilityModel: config?.defaults?.visible || {},
          },
          pagination: defaultPageSize
            ? { paginationModel: { pageSize: defaultPageSize } }
            : undefined,
          sorting: sortModel ? { sortModel } : undefined,
        }
      : undefined;

  const dataGridLocaleText =
    locale === 'tr'
      ? trTR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText;

  const mergedSlotProps = {
    ...slotProps,
    filterPanel: {
      ...slotProps?.filterPanel,
      sx: {
        '& .MuiDataGrid-filterForm': {
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 1.5,
        },
        '& .MuiDataGrid-filterFormColumnInput': { width: 180 },
        '& .MuiDataGrid-filterFormOperatorInput': { width: 200, minWidth: 160 },
        '& .MuiDataGrid-filterFormValueInput': { width: 260, minWidth: 200 },
        '& .MuiDataGrid-filterFormValueInput .MuiInputBase-root, & .MuiDataGrid-filterFormValueInput .MuiSelect-select':
          { fontSize: '0.875rem' },
        ...(slotProps?.filterPanel?.sx || {}),
      },
    },
  };

  return (
    <DataGrid
      apiRef={apiRef}
      rows={rows}
      columns={gridColumns}
      loading={loading}
      localeText={dataGridLocaleText}
      filterMode="client"
      disableColumnFilter={false}
      autoHeight={autoHeight}
      initialState={initialState}
      pageSizeOptions={pageSizeOptions}
      onColumnVisibilityModelChange={onColumnsVisibilityChange}
      density="compact"
      getRowId={(row) => row.id}
      slots={slots}
      slotProps={mergedSlotProps}
      sx={{ ...RESPONSIVE_GRID_SX, ...sx }}
      onRowClick={onRowClick}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      style={{ width: '100%' }}
      {...rest}
    />
  );
};

export default DataGridTable;
